import { useProfileQuery } from '@generated'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { MultipleItemResponse, SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { getAccountPrivileges } from '@lib/kontent/getAccountPrivileges'
import { MembershipHomepage } from '@lib/kontent/models/MembershipHomepage'
import { PrivilegeItem } from '@lib/kontent/models/PrivilegeItem'
import { MembershipExclusiveSectionTitle } from '@components/_membership/ExclusiveSectionTitle'
import { MembershipPageTitle } from '@components/_membership/PageTitle'
import { MembershipHomepageFeaturedItem } from '@components/_membership/homepage/FeaturedItem'
import { MembershipHomeFeaturedProducts } from '@components/_membership/homepage/FeaturedProducts'
import { useLoyaltyPrivileges } from '@components/_membership/hooks/use-loyalty-privileges'
import { MembershipPage } from '@components/_membership/page'
import { MembershipPrivilegeSlider } from '@components/_membership/privilege-slider'

type MembershipOverviewProps = SingleItemResponse<MembershipHomepage> & {
  privileges: MultipleItemResponse<PrivilegeItem>
}

export const MembershipOverview: FC<MembershipOverviewProps> = ({
  item,
  modular_content,
  privileges,
}) => {
  const [{ data }] = useProfileQuery()
  const { availablePrivileges } = useLoyaltyPrivileges(privileges)
  const { t } = useTranslation('other')
  let name = ''

  if (data?.me?.dafUser?.voornaam) {
    name = data.me.dafUser.voornaam
  }

  return (
    <MembershipPage pageType="overview">
      <MembershipPageTitle title={`${t('welcome')}${name && `, ${name}`}`}>
        <MembershipExclusiveSectionTitle variant="homepage" />
      </MembershipPageTitle>
      <div className="flex flex-col gap-10 md:gap-16 md:pt-8 lg:gap-24">
        <MembershipHomepageFeaturedItem modularContent={modular_content} item={item.elements} />
        <MembershipPrivilegeSlider
          title={t('membershipFeaturedPrivilegesTitle')}
          privileges={availablePrivileges}
          modularContent={privileges.modular_content}
        />
        <MembershipHomeFeaturedProducts />
      </div>
    </MembershipPage>
  )
}

export const getStaticProps: GetStaticProps = async ({ preview, locale }) => {
  try {
    const content = await getPageByCodename<MembershipHomepage>({
      codename: 'membership___homepage',
      lang: locale as string,
      preview,
    })

    const privileges = await getAccountPrivileges({
      language: locale as string,
      preview,
    })

    return {
      props: {
        ...(await getServerSideTranslations(locale as string)),
        privileges: privileges as MultipleItemResponse<PrivilegeItem>,
        ...content,
      },
    }
  } catch (error) {
    throw error
  }
}

export default MembershipOverview
